import styles from '../SeriveContractBillingOverlayController.module.scss';

import { CardHeader, Stack, Theme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { Card } from '@work4all/components/lib/dataDisplay/card';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';

import { useUser } from '@work4all/data';

import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';

import { ControllerPlus } from '../../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../../form-plus/use-form-context-plus';
import { DateTimeInputPicker, LabeledInput } from '../../../../locked-inputs';
import { ControlWrapper } from '../../../components';
import { ServiceContractsBillingsFormValue } from '../types';

import { BzObjTypePickerField } from './components/bzObjType-picker/BzObjTypePickerField';
import { CheckboxItem } from './components/CheckboxItem';

const BZ_OBJ_TYPE_PICKER_FIELD_DISABLED_KEYS = [
  BzObjType.UNKNOWN,
  BzObjType.KALKULATION,
  BzObjType.AUFTRAG,
  BzObjType.ANGEBOT,
  BzObjType.LIEFERSCHEIN,
  BzObjType.BESTELLUNG,
  BzObjType.BEDARFSANFORDERUNG,
  BzObjType.EINGANGSLIEFERSCHEIN,
];

export const HeaderData = () => {
  const { t } = useTranslation();
  const { register, control } =
    useFormContextPlus<ServiceContractsBillingsFormValue>();

  const user = useUser();
  const hasAdminRights = user.isMaster;

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  return (
    <Card className={styles.card}>
      <CardHeader
        title={t('MASK.BILLING.HEADER_DATA')}
        className={styles.cardHeader}
      />
      <ControlWrapper columns={isMobile ? 1 : 4} className={styles.cardContent}>
        <ControllerPlus
          control={control}
          name="bzObjType"
          render={({ field }) => (
            <BzObjTypePickerField
              label={t('COMMON.KIND')}
              {...field}
              disabledKeys={BZ_OBJ_TYPE_PICKER_FIELD_DISABLED_KEYS}
            />
          )}
        />
        <ControllerPlus
          control={control}
          name="invoiceDate"
          render={({ field }) => {
            return (
              <DateTimeInputPicker
                {...field}
                value={new Date(field.value)}
                dateLabel={t('MASK.BILLING.INPUTS.DOCUMENT_DATE')}
                clearable={false}
                withTime={false}
              />
            );
          }}
        />
        <ControllerPlus
          control={control}
          name="accountingDate"
          render={({ field }) => {
            return (
              <DateTimeInputPicker
                {...field}
                value={new Date(field.value)}
                dateLabel={t('MASK.BILLING.INPUTS.BOOKING_DATE')}
                clearable={false}
                withTime={false}
              />
            );
          }}
        />
        <Tooltip
          title={!hasAdminRights ? t('MASK.BILLING.ADMIN_RIGHTS_TOOLTIP') : ''}
          activateForDisabled
        >
          <LabeledInput
            label={t('MASK.BILLING.INPUTS.TERM')}
            {...register('batchBillingRunName')}
            disabled={!hasAdminRights}
          />
        </Tooltip>
      </ControlWrapper>
      <ControlWrapper columns={isMobile ? 1 : 2}>
        <ControlWrapper
          columns={isMobile ? 1 : 2}
          className={styles.cardContent}
        >
          <Stack>
            <Divider
              style={{ flex: '1', marginRight: '1rem' }}
              title={t('MASK.BILLING.HEADER_TEXT')}
            />
            <ControllerPlus
              control={control}
              name="headerShowBatchBillingRunName"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.RUN_NAME')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
            <ControllerPlus
              control={control}
              name="headerShowObjectName"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.OBJECT_NAME')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
            <ControllerPlus
              control={control}
              name="headerShowPeriod"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.PERIOD')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
            <ControllerPlus
              control={control}
              name="headerShowContractTypeAlias"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.HEADER_CONTRACT_TYPE_ALIAS')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
            <ControllerPlus
              control={control}
              name="headerShowContractNumber"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.CONTRACT_NUMBER')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
          </Stack>
          <Stack>
            <Divider
              style={{ flex: '1', marginRight: '1rem' }}
              title={t('MASK.BILLING.CLOSING_TEXT')}
            />
            <ControllerPlus
              control={control}
              name="internalNoteShowBatchBillingRunName"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.RUN_NAME')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
            <ControllerPlus
              control={control}
              name="internalNoteShowObjectName"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.OBJECT_NAME')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
            <ControllerPlus
              control={control}
              name="internalNoteShowPeriod"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.PERIOD')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
            <ControllerPlus
              control={control}
              name="internalNoteShowContractTypeAlias"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.HEADER_CONTRACT_TYPE_ALIAS')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
            <ControllerPlus
              control={control}
              name="internalNoteShowContractNumber"
              render={({ field: { value: checked, ...field } }) => (
                <CheckboxItem
                  label={t('MASK.BILLING.INPUTS.CONTRACT_NUMBER')}
                  checked={checked}
                  disabled={!hasAdminRights}
                  {...field}
                />
              )}
            />
          </Stack>
        </ControlWrapper>
        <Stack className={styles.cardContent}>
          <Divider style={{ marginRight: '1rem' }} title={t('MASK.OPTIONS')} />
          <ControllerPlus
            control={control}
            name="useDefaultTextFromCustomer"
            render={({ field: { value: checked, ...field } }) => (
              <CheckboxItem
                label={t('MASK.BILLING.INPUTS.DEFAULT_TEXT')}
                checked={checked}
                disabled={!hasAdminRights}
                {...field}
              />
            )}
          />
          <ControllerPlus
            control={control}
            name="setNewBillingDate"
            render={({ field: { value: checked, ...field } }) => (
              <CheckboxItem
                label={t('MASK.BILLING.INPUTS.BILLING_DATE')}
                checked={checked}
                disabled={!hasAdminRights}
                {...field}
              />
            )}
          />
          {/* Currently not defined in BE */}
          {/* <ControllerPlus
            control={control}
            name="summarizePerReceipient"
            render={({ field: { value: checked, ...field } }) => (
              <CheckboxItem
                label={t('MASK.BILLING.INPUTS.SUMMARIZE_PER_RECEIPIENT')}
                checked={checked}
                disabled // Currently not defined
                {...field}
              />
            )}
          />
          <ControllerPlus
            control={control}
            name="summarizeNote"
            render={({ field: { value: checked, ...field } }) => (
              <CheckboxItem
                label={t('MASK.BILLING.INPUTS.SUMMARIZE_NOTE')}
                checked={checked}
                disabled // Currently not defined
                {...field}
              />
            )}
          /> */}
        </Stack>
      </ControlWrapper>
    </Card>
  );
};
